import { supabase } from '../../config/supabase';

/**
 * Get all drivers for a company
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of drivers
 */
export const getCompanyDrivers = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('users')
            .select('id, username, full_name, email, phone, role, status')
            .eq('company_id', companyId)
            .eq('role', 'driver')
            .eq('status', 'active')
            .order('username', { ascending: true });

        if (error) throw error;

        return data || [];
    } catch (error) {
        console.error('Error fetching company drivers:', error);
        throw error;
    }
};

/**
 * Assign a driver to a delivery
 * @param {string} deliveryId - The job_deliveries ID
 * @param {string} driverId - The driver's user ID
 * @param {string} driverName - The driver's name
 * @param {string} assignedBy - The user who assigned the driver
 * @returns {Promise<Object>} Updated delivery
 */
export const assignDriverToDelivery = async (deliveryId, driverId, driverName, assignedByUserId, assignedByName) => {
    try {
        const { data, error } = await supabase
            .from('job_deliveries')
            .update({
                assigned_driver_id: driverId,
                assigned_driver_name: driverName,
                assigned_at: new Date().toISOString(),
                assigned_by_user_id: assignedByUserId,
                assigned_by_name: assignedByName,
                delivery_status: 'scheduled' // Update status when driver is assigned
            })
            .eq('id', deliveryId)
            .select()
            .single();

        if (error) throw error;

        return data;
    } catch (error) {
        console.error('Error assigning driver to delivery:', error);
        throw error;
    }
};

/**
 * Remove driver from a delivery
 * @param {string} deliveryId - The job_deliveries ID
 * @returns {Promise<Object>} Updated delivery
 */
export const removeDriverFromDelivery = async (deliveryId) => {
    try {
        const { data, error } = await supabase
            .from('job_deliveries')
            .update({
                assigned_driver_id: null,
                assigned_driver_name: null,
                assigned_at: null,
                assigned_by_user_id: null,
                assigned_by_name: null,
                delivery_status: 'not_scheduled' // Reset status when driver is removed
            })
            .eq('id', deliveryId)
            .select()
            .single();

        if (error) throw error;

        return data;
    } catch (error) {
        console.error('Error removing driver from delivery:', error);
        throw error;
    }
};

/**
 * Get driver workload (count of assigned deliveries)
 * @param {string} driverId - The driver's user ID
 * @param {string} companyId - The company ID
 * @returns {Promise<number>} Count of assigned deliveries
 */
export const getDriverWorkload = async (driverId, companyId) => {
    try {
        const { count, error } = await supabase
            .from('job_deliveries')
            .select('id', { count: 'exact', head: true })
            .eq('assigned_driver_id', driverId)
            .neq('delivery_status', 'delivered')
            .neq('delivery_status', 'cancelled');

        if (error) throw error;

        return count || 0;
    } catch (error) {
        console.error('Error getting driver workload:', error);
        return 0;
    }
};

/**
 * Get driver's assigned deliveries with job details
 * @param {string} driverId - The driver's user ID
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of deliveries with job info
 */
export const getDriverDeliveries = async (driverId, companyId) => {
    try {
        const { data, error } = await supabase
            .from('job_deliveries')
            .select(`
                *,
                jobs!inner (
                    id,
                    unique_id,
                    customer_name,
                    customer_address,
                    company_id
                )
            `)
            .eq('assigned_driver_id', driverId)
            .eq('jobs.company_id', companyId)
            .neq('delivery_status', 'delivered')
            .neq('delivery_status', 'cancelled')
            .order('delivery_date', { ascending: true, nullsFirst: false });

        if (error) throw error;

        return data || [];
    } catch (error) {
        console.error('Error getting driver deliveries:', error);
        throw error;
    }
};

/**
 * Subscribe to driver assignment changes
 * @param {string} companyId - Company ID
 * @param {Function} callback - Callback function when changes occur
 * @returns {RealtimeChannel} Supabase channel
 */
export const subscribeToDriverAssignments = (companyId, callback) => {
    const channel = supabase
        .channel(`driver-assignments-${companyId}-${Date.now()}`)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'job_deliveries',
            },
            (payload) => {
                console.log('Driver assignment update:', payload);
                callback(payload);
            }
        )
        .subscribe();

    return channel;
};
